import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { categories, mainSiteUrl } from "@/mocks/categories";

export default function Hero() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-primary-50 via-background-50 to-background-50">
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-accent-100/70"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -left-24 top-40 h-64 w-64 rounded-full bg-secondary-100/60"
        aria-hidden="true"
      />

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-4 py-12 md:px-6 md:py-20 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
            <i className="ri-sparkling-2-line text-sm" />
            {t("hero.badge")}
          </span>

          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
            {t("hero.title")}
          </h1>

          <p className="mt-5 max-w-xl text-base leading-relaxed text-foreground-700 md:text-lg">
            {t("hero.subtitle")}
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 cursor-pointer transition-colors hover:bg-primary-600"
            >
              {t("hero.ctaSpreadsheet")}
              <i className="ri-arrow-right-up-line text-base" />
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 cursor-pointer transition-colors hover:bg-accent-600"
            >
              {t("hero.ctaBuyNow")}
              <i className="ri-shopping-bag-3-line text-base" />
            </a>
          </div>

          <div className="relative mt-8 max-w-md" ref={ref}>
            <label className="block text-sm font-medium text-foreground-700">
              {t("hero.dropdownLabel")}
            </label>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="mt-2 flex w-full items-center justify-between rounded-md border border-background-300/70 bg-background-50 px-4 py-3 text-left text-sm text-foreground-800 cursor-pointer transition-colors hover:bg-background-100"
              aria-haspopup="listbox"
              aria-expanded={open}
            >
              <span className="text-foreground-500">{t("hero.dropdownPlaceholder")}</span>
              <span className="flex h-5 w-5 items-center justify-center">
                <i className={`ri-arrow-down-s-line text-base text-foreground-500 transition-transform ${open ? "rotate-180" : ""}`} />
              </span>
            </button>

            {open && (
              <div className="absolute z-30 mt-2 max-h-72 w-full max-w-md overflow-y-auto rounded-lg border border-background-300/70 bg-background-50 p-1">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                  >
                    {t(cat.key)}
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-up-line text-sm text-foreground-400" />
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <p className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            {t("hero.trust")}
          </p>
        </div>

        <div className="relative">
          <div className="overflow-hidden rounded-lg border border-background-300/60 bg-background-50">
            <img
              src="https://readdy.ai/api/search-image?query=Bright%20clean%20editorial%20product%20photography%20of%20neatly%20arranged%20stylish%20sneakers%20hoodies%20and%20caps%20on%20a%20light%20warm%20neutral%20background%20with%20soft%20natural%20lighting%20minimalist%20fresh%20aesthetic%20gentle%20shadows%20airy%20composition%20ecommerce%20lifestyle%20style&width=900&height=900&seq=hero-products-01&orientation=squarish"
              alt="USFans Spreadsheet store products"
              title="USFans Spreadsheet products"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="absolute -bottom-4 -left-4 hidden rounded-lg border border-background-300/60 bg-background-50 px-4 py-3 md:block">
            <p className="text-xs font-medium text-foreground-500">{t("hero.badge")}</p>
            <p className="mt-1 font-heading text-sm font-bold text-foreground-950">
              {t("content.eyebrow")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}